import { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useSession } from 'contexts/session-context'
import useRouter from 'hooks/use-router'
import useRedirect from 'hooks/use-redirect'
import Hero from './components/Hero'
import CommunitySection from './components/CommunitySection'
import ViewingPartyBanner from './components/ViewingPartyBanner'
import CampaignVideo from './components/CampaignVideo'
import HowToParticipate from './components/HowToParticipate'
import CalendarSection from './components/CalendarSection'
import SurveyFormSection from './components/SurveyFormSection'
import SuccessSection from './components/SuccessSection'
import CtaVideoSection from './components/CtaVideoSection'
import ComingNextSection from './components/ComingNextSection'
import { useNeighbarsSurvey } from './hooks/use-neighbars-survey'
import { NEIGHBARS_ASSETS } from './constants'

const SEO_TITLE = 'Neighbars | My Heineken'
const SEO_DESCRIPTION =
  'Registra tu edificio y gana una Viewing Party de la UEFA Champions League en el área común para disfrutar con tus vecinos.'

// Deep link to the survey form: /neighbars#formulario
const FORM_ANCHOR_ID = 'formulario'
const FORM_HASH = `#${FORM_ANCHOR_ID}`
// Fallback anchor for non-logged visitors (the VP banner with the CTA)
const VP_BANNER_ANCHOR_ID = 'viewing-party'

const Neighbars = () => {
  const [{ user }] = useSession()
  const { push, pathname } = useRouter()
  const [, { setRedirect }] = useRedirect()
  const { survey, status, submitAnswers } = useNeighbarsSurvey()

  const isLogged = user !== undefined

  // Persist the campaign page as the post-auth destination before leaving:
  // /callback restores it via internal_redirect (see hooks/use-redirect).
  const goToStart = () => {
    setRedirect(pathname)
    push('/login-manager?tointernal=true')
  }

  const handleViewingPartyCta = () => {
    if (!isLogged) {
      goToStart()
      return
    }
    document.getElementById(FORM_ANCHOR_ID)?.scrollIntoView({ behavior: 'smooth' })
  }

  // Deep-link handling for /neighbars#formulario:
  // - Logged users: scroll to the survey form once it mounts.
  // - Non-logged users: scroll to the Viewing Party banner (CTA to log in).
  useEffect(() => {
    if (window.location.hash !== FORM_HASH) return

    // Remove the hash so the browser's native anchor resolution doesn't
    // fight with our programmatic scroll.
    window.history.replaceState(null, '', window.location.pathname)

    const scrollTo = (id: string) => {
      // Small delay to let the page layout settle (images, lazy sections).
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
      }, 400)
    }

    if (!isLogged) {
      scrollTo(VP_BANNER_ANCHOR_ID)
      return
    }

    if (status !== 'form') return
    scrollTo(FORM_ANCHOR_ID)
  }, [status, isLogged])

  return (
    <div className="neighbars">
      <Helmet>
        <title>{SEO_TITLE}</title>
        <meta name="description" content={SEO_DESCRIPTION} />
        <meta property="og:title" content={SEO_TITLE} />
        <meta property="og:description" content={SEO_DESCRIPTION} />
        <meta property="og:image" content={`${window.location.origin}${NEIGHBARS_ASSETS}/neighbars-hero_imagen-mobile.webp`} />
        <meta property="og:type" content="website" />
      </Helmet>
      <Hero />
      <CommunitySection />
      <ViewingPartyBanner id={VP_BANNER_ANCHOR_ID} onCtaClick={handleViewingPartyCta} />
      <CampaignVideo />
      <HowToParticipate />
      <CalendarSection />
      {isLogged && status === 'form' && survey && (
        <SurveyFormSection survey={survey} onSubmit={submitAnswers} />
      )}
      {isLogged && status === 'answered' && <SuccessSection />}
      <CtaVideoSection />
      <ComingNextSection showCta={!isLogged} onCtaClick={goToStart} />
    </div>
  )
}

export default Neighbars
